import { Component, OnInit, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { AuthService } from '../../../../../services/auth.service';
import {
  ChatGeneratedFile,
  InteligenciaArtificialService,
} from '../../../services/inteligencia-artificial.service';

interface ChatAttachment {
  nomeArquivo: string;
  mimeType: string;
  conteudoBase64: string;
}

interface QuickAction {
  label: string;
  icon: string;
  prompt: string;
  requiresSuperAdmin?: boolean;
}

export interface ChatMessage {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  safeContent?: SafeHtml;
  attachment?: ChatAttachment;
  renderVersion?: number;
  timestamp: Date;
}

interface PersistedChatMessage {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

interface PersistedChatState {
  sessionId: string | null;
  messages: PersistedChatMessage[];
}

@Component({
  selector: 'app-inteligencia-artificial',
  standalone: false,
  templateUrl: './inteligencia-artificial.component.html',
  styleUrls: ['./inteligencia-artificial.component.css'],
})
export class InteligenciaArtificialComponent implements OnInit, AfterViewChecked {
  private static readonly STORAGE_KEY_PREFIX = 'iaChatState';
  private static readonly RENDER_VERSION = 12;

  @ViewChild('chatBody') chatBody!: ElementRef;
  @ViewChild('messageInput') messageInput?: ElementRef<HTMLTextAreaElement>;

  chatForm!: FormGroup;
  messages: ChatMessage[] = [];
  isTyping = false;
  displayName = '';
  private sessionId: string | null = null;
  private messageIdCounter = 0;
  private shouldScroll = false;

  readonly quickActions: QuickAction[] = [
    { label: 'Resumo do dia',       icon: 'fas fa-chart-bar',           prompt: 'Me dê um resumo detalhado dos serviços de hoje.' },
    { label: 'Pendências',          icon: 'fas fa-clock',               prompt: 'Quais são os serviços ainda pendentes no mês atual?' },
    { label: 'Performance',         icon: 'fas fa-trophy',              prompt: 'Como está a performance dos colaboradores no mês atual?' },
    { label: 'Relatório Geral',     icon: 'fas fa-file-lines',          prompt: 'Gerar relatório geral para o mês atual.' },
    { label: 'Relatório Financeiro', icon: 'fas fa-list-check',         prompt: 'Gerar relatório financeiro para o mês atual.', requiresSuperAdmin: true },
    { label: 'Relatório Ranking', icon: 'fas fa-ranking-star',        prompt: 'Gerar relatório ranking vistoriadores para o mês atual.' },
    // { label: 'Paradas +07 dias',    icon: 'fas fa-hourglass-half',      prompt: 'Liste as vistorias paradas há mais de 07 dias' },
  ];

  get visibleQuickActions(): QuickAction[] {
    const isSuperAdmin = this.authService.isSuperAdmin();
    return this.quickActions.filter((action) => !action.requiresSuperAdmin || isSuperAdmin);
  }

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private iaService: InteligenciaArtificialService,
    private sanitizer: DomSanitizer,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.chatForm = this.fb.group({ message: ['', Validators.required] });

    const u = this.authService.getUsuario();
    if (u) {
      this.displayName = (u.nomeUsuario || u.userName || u.nome || u.user || '') as string;
    }

    if (this.restoreChatState()) {
      this.shouldScroll = true;
      return;
    }

    const firstName = this.displayName ? ', ' + this.displayName.split(' ')[0] : '';
    this.addMessage(
      'assistant',
      `Olá${firstName}! Sou o assistente de Inteligência Artificial do VibeTex. Posso ajudar com análises, relatórios e dúvidas sobre os serviços de campo. Como posso te ajudar hoje?`,
    );
  }

  ngAfterViewChecked(): void {
    if (this.needsAssistantMessageRefresh()) {
      this.refreshCurrentAssistantMessages();
    }

    if (this.shouldScroll) {
      this.scrollToBottom();
      this.shouldScroll = false;
    }
  }

  private refreshCurrentAssistantMessages(): void {
    let changed = false;

    this.messages = this.messages.map((message) => {
      if (message.role !== 'assistant' || message.renderVersion === InteligenciaArtificialComponent.RENDER_VERSION) {
        return message;
      }

      const formatted = this.formatMessage(message.content);
      const safeContent = this.sanitizer.bypassSecurityTrustHtml(formatted);
      changed = true;

      return {
        ...message,
        safeContent,
        renderVersion: InteligenciaArtificialComponent.RENDER_VERSION,
      };
    });

    if (changed) {
      this.persistChatState();
      this.shouldScroll = true;
    }
  }

  private needsAssistantMessageRefresh(): boolean {
    return this.messages.some(
      (message) => message.role === 'assistant' && message.renderVersion !== InteligenciaArtificialComponent.RENDER_VERSION,
    );
  }

  private scrollToBottom(): void {
    try {
      const el = this.chatBody.nativeElement;
      el.scrollTop = el.scrollHeight;
    } catch {}
  }

  private addMessage(role: 'user' | 'assistant', content: string, attachment?: ChatAttachment): void {
    let safeContent: SafeHtml | undefined = undefined;
    
    if (role === 'assistant') {
      const formatted = this.formatMessage(content);
      safeContent = this.sanitizer.bypassSecurityTrustHtml(formatted);
    }
    
    this.messages.push({ id: ++this.messageIdCounter, role, content, safeContent, attachment, timestamp: new Date() });
    if (role === 'assistant') {
      this.messages[this.messages.length - 1].renderVersion = InteligenciaArtificialComponent.RENDER_VERSION;
    }
    this.persistChatState();
    this.shouldScroll = true;
  }

  downloadAttachment(message: ChatMessage): void {
    const attachment = message.attachment;
    if (!attachment) return;

    try {
      const base64 = attachment.conteudoBase64.replace(/\s/g, '');
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);

      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }

      const blob = new Blob([bytes], { type: attachment.mimeType });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.nomeArquivo;
      link.click();

      URL.revokeObjectURL(url);
    } catch {
      this.addMessage('assistant', 'Nao foi possivel preparar o arquivo para download. Tente gerar o relatorio novamente.');
    }
  }

  private formatMessage(value: string): string {
    if (!value) return '';

    return this.formatMarkdownBlocks(value.trim());
  }

  private formatMarkdownBlocks(text: string): string {
    const lines = text.split(/\r?\n/);
    const html: string[] = [];
    let inUnorderedList = false;
    let inOrderedList = false;
    let paragraphCount = 0;
    let lastListHeader = '';
    let i = 0;

    const closeLists = (): void => {
      if (inUnorderedList) {
        html.push('</ul>');
        inUnorderedList = false;
      }

      if (inOrderedList) {
        html.push('</ol>');
        inOrderedList = false;
      }
    };

    while (i < lines.length) {
      const rawLine = lines[i];
      const line = rawLine.trim();

      if (!line) {
        closeLists();
        i++;
        continue;
      }

      if (/^--+$/.test(line)) {
        closeLists();
        html.push('<hr class="ia-md-divider" />');
        i++;
        continue;
      }

      const heading = this.parseHeadingLine(line);
      if (heading) {
        closeLists();
        const escapedHeading = this.escapeHtml(heading.text);
        html.push(`<h${heading.level} class="ia-md-heading ia-md-heading-${heading.level}">${this.formatInline(escapedHeading)}</h${heading.level}>`);
        i++;
        paragraphCount = 0;
        continue;
      }

      // Detectar tabela markdown
      if (this.isMarkdownTableRow(line)) {
        closeLists();
        const tableHtml = this.parseMarkdownTable(lines, i);
        if (tableHtml.html) {
          html.push(tableHtml.html);
          i = tableHtml.nextIndex;
          paragraphCount = 0;
          continue;
        }
      }

      const multiStructuredHtml = this.parseMultiStructuredDataTable(lines, i);
      if (multiStructuredHtml.html) {
        closeLists();
        html.push(multiStructuredHtml.html);
        i = multiStructuredHtml.nextIndex;
        paragraphCount = 0;
        continue;
      }

      const rankingTableHtml = this.parseVistoriadorRankingTable(lines, i);
      if (rankingTableHtml.html) {
        closeLists();
        html.push(rankingTableHtml.html);
        i = rankingTableHtml.nextIndex;
        paragraphCount = 0;
        continue;
      }

      const numberedKvTableHtml = this.parseNumberedKvVistoriaTable(lines, i);
      if (numberedKvTableHtml.html) {
        closeLists();
        html.push(numberedKvTableHtml.html);
        i = numberedKvTableHtml.nextIndex;
        paragraphCount = 0;
        continue;
      }

      const serviceListTableHtml = this.parseNumberedServiceListTable(lines, i);
      if (serviceListTableHtml.html) {
        closeLists();
        html.push(serviceListTableHtml.html);
        i = serviceListTableHtml.nextIndex;
        paragraphCount = 0;
        continue;
      }

      const keyValueTableHtml = this.parseKeyValueBulletTable(lines, i);
      if (keyValueTableHtml.html) {
        closeLists();
        html.push(keyValueTableHtml.html);
        i = keyValueTableHtml.nextIndex;
        paragraphCount = 0;
        continue;
      }

      const sectionMetricTableHtml = this.parseSectionMetricTable(lines, i);
      if (sectionMetricTableHtml.html) {
        closeLists();
        html.push(sectionMetricTableHtml.html);
        i = sectionMetricTableHtml.nextIndex;
        paragraphCount = 0;
        continue;
      }

      const vistoriaTableHtml = this.parseVistoriaListTable(lines, i);
      if (vistoriaTableHtml.html) {
        closeLists();
        html.push(vistoriaTableHtml.html);
        i = vistoriaTableHtml.nextIndex;
        paragraphCount = 0;
        continue;
      }

      const looseListingTableHtml = this.parseLooseListingTable(lines, i);
      if (looseListingTableHtml.html) {
        closeLists();
        html.push(looseListingTableHtml.html);
        i = looseListingTableHtml.nextIndex;
        paragraphCount = 0;
        continue;
      }

      const plainListingTableHtml = this.parsePlainListingTable(lines, i);
      if (plainListingTableHtml.html) {
        closeLists();
        html.push(plainListingTableHtml.html);
        i = plainListingTableHtml.nextIndex;
        paragraphCount = 0;
        continue;
      }

      const operationalScenarioCardHtml = this.parseOperationalScenarioCard(lines, i);
      if (operationalScenarioCardHtml.html) {
        closeLists();
        html.push(operationalScenarioCardHtml.html);
        i = operationalScenarioCardHtml.nextIndex;
        paragraphCount = 0;
        continue;
      }

      const unorderedMatch = line.match(/^[*-]\s+(.*)$/);
      if (unorderedMatch) {
        if (inOrderedList) {
          html.push('</ol>');
          inOrderedList = false;
        }

        const rawItemContent = unorderedMatch[1];

        // Track list group headers like "Vistorias de Recadastro:" to use as origem context
        const headerMatch = rawItemContent.replace(/\*\*/g, '').match(/^Vistorias?\s+de\s+(.+?):\s*$/i);
        if (headerMatch) {
          lastListHeader = headerMatch[1].trim();
        }

        // Detect vistoria card patterns
        const vistoriaCard = this.tryRenderVistoriaCard(rawItemContent, lastListHeader);
        if (vistoriaCard) {
          if (inUnorderedList) { html.push('</ul>'); inUnorderedList = false; }
          html.push(vistoriaCard);
          i++;
          continue;
        }

        if (!inUnorderedList) {
          html.push('<ul class="ia-md-list">');
          inUnorderedList = true;
        }

        const escapedContent = this.escapeHtml(rawItemContent);
        html.push(`<li>${this.formatInline(escapedContent)}</li>`);
        i++;
        continue;
      }

      const orderedMatch = line.match(/^\d+\.\s+(.*)$/);
      if (orderedMatch) {
        if (inUnorderedList) {
          html.push('</ul>');
          inUnorderedList = false;
        }

        const rawItemContent = orderedMatch[1];

        // Detect structured key:value SQL result rows and render as data card
        if (this.isStructuredDataRow(rawItemContent)) {
          closeLists();
          html.push(this.renderStructuredDataCard(rawItemContent));
          i++;
          paragraphCount = 0;
          continue;
        }

        if (!inOrderedList) {
          html.push('<ol class="ia-md-list ia-md-list-ordered">');
          inOrderedList = true;
        }

        const escapedContent = this.escapeHtml(orderedMatch[1]);
        html.push(`<li>${this.formatInline(escapedContent)}</li>`);
        i++;
        continue;
      }

      closeLists();
      paragraphCount++;
      const paragraphClasses = ['ia-md-paragraph'];

      if (paragraphCount === 1) {
        paragraphClasses.push('ia-md-lead');
      }

      if (this.isHighlightLine(line)) {
        paragraphClasses.push('ia-md-highlight');
      }

      if (this.isRequestGuidanceLine(line)) {
        paragraphClasses.push('ia-md-request');
      }

      const escapedLine = this.escapeHtml(line);
      html.push(`<p class="${paragraphClasses.join(' ')}">${this.formatInline(escapedLine)}</p>`);

      const optionsListHtml = this.buildReportOptionsListHtml(line);
      if (optionsListHtml) {
        html.push(optionsListHtml);
      }

      i++;
    }

    closeLists();
    return html.join('');
  }

  private isMarkdownTableRow(line: string): boolean {
    return /^\|(.+)\|$/.test(line);
  }

  private parseMarkdownTable(lines: string[], startIndex: number): { html: string; nextIndex: number } {
    if (startIndex >= lines.length) {
      return { html: '', nextIndex: startIndex };
    }

    const headerLine = lines[startIndex].trim();
    if (!this.isMarkdownTableRow(headerLine)) {
      return { html: '', nextIndex: startIndex };
    }

    // Aceita tabela markdown com ou sem linha separadora padrao.
    const separatorLine = startIndex + 1 < lines.length ? lines[startIndex + 1].trim() : '';
    const hasSeparator = /^\|[\s\-|:]+\|$/.test(separatorLine);

    const headerCells = this.parseRawTableRow(headerLine);
    const headerColumnKeys = headerCells.map((header) => this.mapMarkdownHeaderToColumnKey(header));
    const isListingTable = this.isListingMarkdownTable(headerColumnKeys);

    const wrapperClass = isListingTable ? 'ia-table-wrapper ia-table-wrapper-listing' : 'ia-table-wrapper';
    const tableClass = isListingTable ? 'ia-md-table ia-md-table-listing' : 'ia-md-table';

    const htmlTable: string[] = [`<div class="${wrapperClass}"><table class="${tableClass}">`];
    
    // Header
    htmlTable.push('<thead><tr>');
    for (let h = 0; h < headerCells.length; h++) {
      const header = this.escapeHtml(headerCells[h]);
      const colKey = headerColumnKeys[h];
      const thClass = colKey ? ` class="ia-col-${colKey}"` : '';
      htmlTable.push(`<th${thClass}>${header}</th>`);
    }
    htmlTable.push('</tr></thead>');

    // Body
    htmlTable.push('<tbody>');
    let i = startIndex + (hasSeparator ? 2 : 1);
    let parsedRows = 0;
    while (i < lines.length) {
      const rowLine = lines[i].trim();
      if (!this.isMarkdownTableRow(rowLine)) {
        break;
      }

      const rawCells = this.parseRawTableRow(rowLine);
      htmlTable.push('<tr>');
      for (let c = 0; c < headerCells.length; c++) {
        const cellValue = rawCells[c] ?? '';
        const colKey = headerColumnKeys[c];

        if (colKey === 'numeroordem' || colKey === 'status' || colKey === 'datadistribuicao') {
          htmlTable.push(this.renderTableCell(colKey, cellValue));
          continue;
        }

        if (colKey) {
          htmlTable.push(`<td class="ia-col-${colKey}">${this.escapeHtml(cellValue)}</td>`);
        } else {
          htmlTable.push(`<td>${this.escapeHtml(cellValue)}</td>`);
        }
      }
      htmlTable.push('</tr>');
      parsedRows++;
      i++;
    }

    if (parsedRows === 0) {
      return { html: '', nextIndex: startIndex };
    }

    htmlTable.push('</tbody></table></div>');

    return {
      html: htmlTable.join(''),
      nextIndex: i,
    };
  }

  private parseTableRow(line: string): string[] {
    return line
      .trim()
      .split('|')
      .slice(1, -1)
      .map((cell) => this.escapeHtml(cell.trim()));
  }

  private parseRawTableRow(line: string): string[] {
    return line
      .trim()
      .split('|')
      .slice(1, -1)
      .map((cell) => cell.trim());
  }

  private mapMarkdownHeaderToColumnKey(header: string): string | null {
    const normalized = header
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]/g, '')
      .trim();

    if (!normalized) return null;
    // Detecta número da vistoria/ordem, mas NÃO "nomevistoriador" nem "nometecnico"
    if ((normalized.includes('vistoria') || normalized.includes('ordem')) && !normalized.includes('nome') && !normalized.includes('vistoriador')) return 'numeroordem';
    if (normalized.includes('status')) return 'status';
    if (normalized.includes('origem')) return 'origem';
    if (normalized.includes('diasparado') || normalized === 'dias') return 'diasparado';
    if (normalized.includes('datadistribuicao')) return 'datadistribuicao';
    return null;
  }

  private isListingMarkdownTable(columnKeys: Array<string | null>): boolean {
    const keys = columnKeys.filter((k): k is string => !!k);
    return (
      keys.includes('numeroordem') &&
      keys.includes('status') &&
      keys.includes('origem') &&
      keys.includes('diasparado') &&
      keys.includes('datadistribuicao')
    );
  }

  private parseKeyValueBulletTable(lines: string[], startIndex: number): { html: string; nextIndex: number } {
    const rows: Array<Record<string, string>> = [];
    let i = startIndex;

    while (i < lines.length) {
      const currentLine = lines[i].trim();
      if (!currentLine) {
        break;
      }

      const parsedRow = this.parseKeyValueRow(currentLine);
      if (!parsedRow) {
        break;
      }

      rows.push(parsedRow);
      i++;
    }

    if (rows.length < 2) {
      return { html: '', nextIndex: startIndex };
    }

    const preferredOrder = ['numeroordem', 'status', 'datadistribuicao', 'nomevistoriador', 'nometecnico'];
    const discoveredKeys: string[] = [];

    for (const row of rows) {
      for (const key of Object.keys(row)) {
        if (!discoveredKeys.includes(key)) {
          discoveredKeys.push(key);
        }
      }
    }

    discoveredKeys.sort((a, b) => {
      const ia = preferredOrder.indexOf(a);
      const ib = preferredOrder.indexOf(b);
      if (ia === -1 && ib === -1) return 0;
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });

    const htmlTable: string[] = ['<div class="ia-table-wrapper"><table class="ia-md-table">'];
    htmlTable.push('<thead><tr>');
    for (const key of discoveredKeys) {
      htmlTable.push(`<th class="ia-col-${key}">${this.getColumnLabelFromKey(key)}</th>`);
    }
    htmlTable.push('</tr></thead><tbody>');

    for (const row of rows) {
      htmlTable.push('<tr>');
      for (const key of discoveredKeys) {
        htmlTable.push(this.renderTableCell(key, row[key] || ''));
      }
      htmlTable.push('</tr>');
    }

    htmlTable.push('</tbody></table></div>');
    return { html: htmlTable.join(''), nextIndex: i };
  }

  private parseVistoriaListTable(lines: string[], startIndex: number): { html: string; nextIndex: number } {
    // Matches: "* Vistoria número 03376 (Origem: Recadastro, Status: Pendente), distribuída em 11/11/2025 e parada há 141 dias."
    // Also matches: "* Vistoria 03376 (Origem: Recadastro) - Status: Pendente. Distribuída em: 11/11/2025 (parada há 142 dias)."
    // Format A: "* Vistoria número **03376** (Origem: X, Status: Y) ... 11/11/2025 ... 142 dias"
    const PATTERN_A = /^[*\-•]\s+(?:\*{0,2})?Vistoria\s+(?:\S+\s+)?\*{0,2}(\d+)\*{0,2}\s*\(Origem:\s*([^,)]+),\s*Status:\s*([^)]+)\).*?(\d{2}\/\d{2}\/\d{4}).*?(\d+)\s+dias/i;
    // Format B: "*   **Vistoria 03376** (Origem: X) - Status: Y. Distribuída em: 11/11/2025 (parada há 142 dias)."
    const PATTERN_B = /^[*\-•]\s+\*{0,2}Vistoria\s+(\d+)\*{0,2}\s*\(Origem:\s*([^)]+)\)\s*[-–]\s*Status:\s*([^.(]+).*?(\d{2}\/\d{2}\/\d{4}).*?(\d+)\s+dias/i;
    // Format C: "* Vistoria 03376, distribuída em 11/11/2025, parada há 142 dias."
    const PATTERN_C = /^[*\-•]\s+\*{0,2}Vistoria\s+(\d+)\*{0,2},?\s+distribu[ií]da\s+em[:\s]+(\d{2}\/\d{2}\/\d{4}),?\s+parada\s+h[aá]\s+(\d+)\s+dias/i;

    const rows: Array<{ numero: string; origem: string; status: string; data: string; dias: string }> = [];
    // Detect context origem from preceding lines (e.g. "Vistorias de Recadastro Pendentes:")
    let contextOrigem = '';
    for (let k = Math.max(0, startIndex - 5); k < startIndex; k++) {
      const ctx = lines[k].toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      if (ctx.includes('recadastro')) { contextOrigem = 'Recadastro'; break; }
      if (ctx.includes('cadastro')) { contextOrigem = 'Cadastro'; break; }
    }

    let i = startIndex;

    while (i < lines.length) {
      const line = lines[i].trim();
      if (!line) break;
      const mA = line.match(PATTERN_A);
      const mB = !mA ? line.match(PATTERN_B) : null;
      const mC = !mA && !mB ? line.match(PATTERN_C) : null;
      if (!mA && !mB && !mC) {
        break;
      }
      if (mC) {
        rows.push({ numero: mC[1], origem: contextOrigem, status: 'Pendente', data: mC[2].trim(), dias: mC[3] });
      } else {
        const m = (mA || mB)!;
        rows.push({ numero: m[1], origem: m[2].trim(), status: m[3].trim(), data: m[4].trim(), dias: m[5] });
      }
      i++;
    }

    if (rows.length < 2) return { html: '', nextIndex: startIndex };

    const badge = (status: string) => {
      const s = status.toLowerCase();
      const sn = s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      const cls = sn.includes('pendente') ? 'ia-badge-pending' : sn.includes('conclu') ? 'ia-badge-done' : sn.includes('neg') ? 'ia-badge-negado' : sn.includes('retornar') || sn.includes('retorno') ? 'ia-badge-retornar' : sn.includes('an') ? 'ia-badge-analysis' : 'ia-badge-default';
      return `<span class="ia-badge ${cls}">${this.escapeHtml(status)}</span>`;
    };

    const badgeOrigem = (origem: string) => {
      if (!origem) return '';
      const o = origem.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      const cls = o.includes('recadastro') ? 'ia-badge-recadastro' : o.includes('cadastro') ? 'ia-badge-cadastro' : 'ia-badge-default';
      return `<span class="ia-badge ${cls}">${this.escapeHtml(origem)}</span>`;
    };

    const diasClass = (d: string) => parseInt(d, 10) >= 30 ? 'ia-dias-critical' : parseInt(d, 10) >= 14 ? 'ia-dias-warn' : 'ia-dias-ok';

    const hasOrigem = rows.some(r => r.origem);
    const pdfPromptVist = `Por favor, gere um PDF com as ${rows.length} vistorias paradas exibidas na última resposta.`;
    let html = `<div class="ia-vistoria-table-wrap">`;
    html += `<div class="ia-table-toolbar"><span class="ia-table-toolbar-title"><i class="fas fa-hourglass-half"></i> ${rows.length} vistorias paradas</span><button class="ia-btn-pdf" data-pdf-prompt="${pdfPromptVist.replace(/"/g, '&quot;')}"><i class="fas fa-file-pdf"></i> Gerar PDF</button></div>`;
    html += `<div class="ia-vistoria-table-body"><table class="ia-vistoria-table"><thead>`;
    html += `<tr class="ia-vt-col-row"><th>Nº</th>${hasOrigem ? '<th style="width:130px">Origem</th>' : ''}<th>Status</th><th>Distribuída em</th><th>Dias</th></tr>`;
    html += `</thead><tbody>`;
    for (const r of rows) {
      html += `<tr>`;
      html += `<td class="ia-vt-num">${this.escapeHtml(r.numero)}</td>`;
      if (hasOrigem) html += `<td>${badgeOrigem(r.origem)}</td>`;
      html += `<td>${badge(r.status)}</td>`;
      html += `<td class="ia-vt-date">${this.escapeHtml(r.data)}</td>`;
      html += `<td class="${diasClass(r.dias)}"><strong>${r.dias}</strong> dias</td>`;
      html += `</tr>`;
    }
    html += `</tbody></table></div></div>`;
    return { html, nextIndex: i };
  }

  /**
   * Handles numbered lists where each item is KV pairs with multi-word keys, e.g.:
   * "1. Numero da Vistoria: 17361, Vistoriador: Paulina, Status da Vistoria: Concluida, Data de Distribuicao: 06/03/2026 08:53:27"
   */
  private parseNumberedKvVistoriaTable(lines: string[], startIndex: number): { html: string; nextIndex: number } {
    const ROW_PATTERN = /^\d+\.\s+Numero\s+da\s+Vistoria:\s*(\d+),\s*Vistoriador:\s*(.+?),\s*Status\s+da\s+Vistoria:\s*(.+?),\s*Data\s+de\s+Distribuicao:\s*(.+)$/i;

    const rows: Array<{ numero: string; vistoriador: string; status: string; data: string }> = [];
    let i = startIndex;

    while (i < lines.length) {
      const line = lines[i].trim();
      if (!line) { i++; continue; }
      const m = line.match(ROW_PATTERN);
      if (!m) break;
      rows.push({ numero: m[1].trim(), vistoriador: m[2].trim(), status: m[3].trim(), data: m[4].trim() });
      i++;
    }

    if (rows.length < 2) return { html: '', nextIndex: startIndex };

    const statusBadge = (val: string): string => {
      const s = val.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      const cls = s.includes('pendente') ? 'ia-badge-pending'
        : s.includes('conclu') ? 'ia-badge-done'
        : s.includes('neg') ? 'ia-badge-negado'
        : s.includes('retornar') || s.includes('retorno') ? 'ia-badge-retornar'
        : s.includes('an') ? 'ia-badge-analysis'
        : 'ia-badge-default';
      return `<span class="ia-badge ${cls}">${this.escapeHtml(val)}</span>`;
    };

    const pdfPrompt = `Por favor, gere um PDF com os ${rows.length} registros da tabela que foi exibida na última resposta.`;
    let html = '<div class="ia-vistoria-table-wrap">';
    html += `<div class="ia-table-toolbar"><span class="ia-table-toolbar-title"><i class="fas fa-table"></i> ${rows.length} registros encontrados</span><button class="ia-btn-pdf" data-pdf-prompt="${pdfPrompt.replace(/"/g, '&quot;')}"><i class="fas fa-file-pdf"></i> Gerar PDF</button></div>`;
    html += '<div class="ia-vistoria-table-body"><table class="ia-vistoria-table ia-vistoria-table-auto"><thead><tr class="ia-vt-col-row">';
    html += '<th>Nº Vistoria</th><th>Vistoriador</th><th>Status</th><th>Data Distribuição</th><th class="ia-vt-col-ver"></th>';
    html += '</tr></thead><tbody>';

    for (const row of rows) {
      const dataAttr = row.data ? ` data-vistoria-data="${this.escapeHtml(row.data)}"` : '';
      html += '<tr>';
      html += `<td class="ia-vt-num">${this.escapeHtml(row.numero)}</td>`;
      html += `<td>${this.escapeHtml(row.vistoriador)}</td>`;
      html += `<td>${statusBadge(row.status)}</td>`;
      html += `<td class="ia-vt-date">${this.escapeHtml(row.data)}</td>`;
      html += `<td class="ia-vt-col-ver"><button class="ia-btn-ver" data-vistoria-numero="${this.escapeHtml(row.numero)}"${dataAttr}>Ver</button></td>`;
      html += '</tr>';
    }

    html += '</tbody></table></div></div>';
    return { html, nextIndex: i };
  }

  private parseNumberedServiceListTable(lines: string[], startIndex: number): { html: string; nextIndex: number } {
    const rows: Array<{ numeroOrdem: string; vistoriador: string; dataDistribuicao: string }> = [];
    let i = startIndex;

    while (i < lines.length) {
      const currentLine = lines[i].trim();
      if (!currentLine) {
        break;
      }

      const match = currentLine.match(
        /^\d+\.\s+\*\*Ordem\s+de\s+Servi[cç]o:\*\*\s*([^,]+),\s*\*\*Vistoriador:\*\*\s*([^,]+),\s*\*\*Data\s+de\s+Distribui[cç][aã]o:\*\*\s*(.+)$/i,
      );

      if (!match) {
        break;
      }

      rows.push({
        numeroOrdem: match[1].trim(),
        vistoriador: match[2].trim(),
        dataDistribuicao: match[3].trim(),
      });

      i++;
    }

    if (rows.length < 2) {
      return { html: '', nextIndex: startIndex };
    }

    const htmlTable: string[] = ['<div class="ia-table-wrapper"><table class="ia-md-table">'];
    htmlTable.push('<thead><tr>');
    htmlTable.push('<th class="ia-col-numeroordem">Numero da Ordem</th>');
    htmlTable.push('<th class="ia-col-nomevistoriador">Vistoriador</th>');
    htmlTable.push('<th class="ia-col-datadistribuicao">Data de Distribuicao</th>');
    htmlTable.push('</tr></thead><tbody>');

    for (const row of rows) {
      htmlTable.push('<tr>');
      htmlTable.push(this.renderTableCell('numeroordem', row.numeroOrdem, row.dataDistribuicao));
      htmlTable.push(this.renderTableCell('nomevistoriador', row.vistoriador));
      htmlTable.push(this.renderTableCell('datadistribuicao', row.dataDistribuicao));
      htmlTable.push('</tr>');
    }

    htmlTable.push('</tbody></table></div>');
    return { html: htmlTable.join(''), nextIndex: i };
  }

  private parseSectionMetricTable(lines: string[], startIndex: number): { html: string; nextIndex: number } {
    if (startIndex >= lines.length) {
      return { html: '', nextIndex: startIndex };
    }

    const headerLine = lines[startIndex].trim();
    const sectionHeaderMatch = headerLine.match(/^\d+\.\s+(.+?):\s*$/);
    if (!sectionHeaderMatch) {
      return { html: '', nextIndex: startIndex };
    }

    const rows: Array<{ key: string; value: string }> = [];
    let i = startIndex + 1;

    while (i < lines.length && !lines[i].trim()) {
      i++;
    }

    while (i < lines.length) {
      const line = lines[i].trim();
      if (!line) {
        if (rows.length > 0) {
          break;
        }
        i++;
        continue;
      }

      if (/^--+$/.test(line)) {
        break;
      }

      const rowMatch = line.match(/^(?:[-*•]\s+)([^:]+):\s*(.+)$/);
      if (!rowMatch) {
        break;
      }

      const rawKey = (rowMatch[1] || '').trim();
      const rawValue = (rowMatch[2] || '').trim();

      if (!rawKey || !rawValue) {
        i++;
        continue;
      }

      const normalizedKey = rawKey
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim();

      if (normalizedKey.startsWith('observacao')) {
        break;
      }

      rows.push({
        key: rawKey,
        value: rawValue,
      });

      i++;
    }

    if (rows.length < 2) {
      return { html: '', nextIndex: startIndex };
    }

    const sectionTitle = this.escapeHtml(sectionHeaderMatch[1].trim());
    const htmlTable: string[] = [
      '<div class="ia-table-wrapper ia-table-wrapper-compact">',
      `<div class="ia-table-title">${sectionTitle}</div>`,
      '<table class="ia-md-table ia-md-table-compact">',
      '<thead><tr><th>Indicador</th><th>Valor</th></tr></thead>',
      '<tbody>',
    ];

    for (const row of rows) {
      const escapedKey = this.escapeHtml(row.key);
      const escapedValue = this.escapeHtml(row.value);
      htmlTable.push('<tr>');
      htmlTable.push(`<td class="ia-col-metric-name">${this.formatInline(escapedKey)}</td>`);
      htmlTable.push(`<td class="ia-col-metric-value">${this.formatInline(escapedValue)}</td>`);
      htmlTable.push('</tr>');
    }

    htmlTable.push('</tbody></table></div>');
    return { html: htmlTable.join(''), nextIndex: i };
  }

  private parsePlainListingTable(lines: string[], startIndex: number): { html: string; nextIndex: number } {
    if (startIndex >= lines.length) {
      return { html: '', nextIndex: startIndex };
    }

    const headerLine = lines[startIndex].trim();
    const normalizedHeader = headerLine
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    const looksLikeListingHeader =
      normalizedHeader.includes('vistoria') &&
      normalizedHeader.includes('status') &&
      normalizedHeader.includes('origem') &&
      normalizedHeader.includes('data de distribuicao');

    if (!looksLikeListingHeader) {
      return { html: '', nextIndex: startIndex };
    }

    const rows: Array<{ numero: string; status: string; origem: string; diasParado: string; dataDistribuicao: string }> = [];
    let i = startIndex + 1;

    while (i < lines.length && !lines[i].trim()) {
      i++;
    }

    while (i < lines.length) {
      const line = lines[i].trim();
      if (!line) {
        if (rows.length > 0) break;
        i++;
        continue;
      }

      if (/^--+$/.test(line) || /^\d+\.\s+/.test(line) || /^#{1,6}\s+/.test(line)) {
        break;
      }

      const rawTokens = line.split(/\s+/).filter(Boolean);
      if (rawTokens.length < 3) {
        break;
      }

      const numero = rawTokens[0] || '';
      const dataDistribuicao = rawTokens[rawTokens.length - 1] || '';

      if (!/^\d{3,8}$/.test(numero) || !/^\d{2}\/\d{2}\/\d{4}$/.test(dataDistribuicao)) {
        break;
      }

      const middle = rawTokens.slice(1, -1).join(' ').trim();
      const parsedMiddle = this.parseListingMiddle(middle);
      if (!parsedMiddle) {
        break;
      }

      rows.push({
        numero,
        status: parsedMiddle.status,
        origem: parsedMiddle.origem,
        diasParado: parsedMiddle.diasParado,
        dataDistribuicao,
      });

      i++;
    }

    if (rows.length < 2) {
      return { html: '', nextIndex: startIndex };
    }

    const htmlTable: string[] = ['<div class="ia-table-wrapper ia-table-wrapper-listing"><table class="ia-md-table ia-md-table-listing">'];
    htmlTable.push('<thead><tr>');
    htmlTable.push('<th class="ia-col-numeroordem">N da Vistoria</th>');
    htmlTable.push('<th class="ia-col-status">Status</th>');
    htmlTable.push('<th class="ia-col-origem">Origem</th>');
    htmlTable.push('<th class="ia-col-diasparado">Dias Parado</th>');
    htmlTable.push('<th class="ia-col-datadistribuicao">Data de Distribuicao</th>');
    htmlTable.push('</tr></thead><tbody>');

    for (const row of rows) {
      htmlTable.push('<tr>');
      htmlTable.push(this.renderTableCell('numeroordem', row.numero, row.dataDistribuicao));
      htmlTable.push(this.renderTableCell('status', row.status));
      htmlTable.push(`<td class="ia-col-origem">${this.escapeHtml(row.origem)}</td>`);
      htmlTable.push(`<td class="ia-col-diasparado">${this.escapeHtml(row.diasParado)}</td>`);
      htmlTable.push(this.renderTableCell('datadistribuicao', row.dataDistribuicao));
      htmlTable.push('</tr>');
    }

    htmlTable.push('</tbody></table></div>');
    return { html: htmlTable.join(''), nextIndex: i };
  }

  private parseLooseListingTable(lines: string[], startIndex: number): { html: string; nextIndex: number } {
    if (startIndex >= lines.length) {
      return { html: '', nextIndex: startIndex };
    }

    const firstLine = lines[startIndex].trim();
    const normalizedFirstLine = firstLine
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    const looksLikeHeader =
      normalizedFirstLine.includes('vistoria') &&
      normalizedFirstLine.includes('status') &&
      normalizedFirstLine.includes('origem');

    const looksLikeRow = this.parseLooseListingRow(firstLine) !== null;

    if (!looksLikeHeader && !looksLikeRow) {
      return { html: '', nextIndex: startIndex };
    }

    let i = looksLikeHeader ? startIndex + 1 : startIndex;
    while (i < lines.length && !lines[i].trim()) {
      i++;
    }

    const rows: Array<{ numero: string; status: string; origem: string; diasParado: string; dataDistribuicao: string }> = [];

    while (i < lines.length) {
      const line = lines[i].trim();
      if (!line) {
        if (rows.length > 0) break;
        i++;
        continue;
      }

      if (/^--+$/.test(line) || /^\|/.test(line) || /^\d+\.\s+/.test(line) || /^#{1,6}\s+/.test(line)) {
        break;
      }

      const parsed = this.parseLooseListingRow(line);
      if (!parsed) {
        if (rows.length === 0) {
          return { html: '', nextIndex: startIndex };
        }
        break;
      }

      rows.push(parsed);
      i++;
    }

    if (rows.length < 2) {
      return { html: '', nextIndex: startIndex };
    }

    const htmlTable: string[] = ['<div class="ia-table-wrapper ia-table-wrapper-listing"><table class="ia-md-table ia-md-table-listing">'];
    htmlTable.push('<thead><tr>');
    htmlTable.push('<th class="ia-col-numeroordem">N da Vistoria</th>');
    htmlTable.push('<th class="ia-col-status">Status</th>');
    htmlTable.push('<th class="ia-col-origem">Origem</th>');
    htmlTable.push('<th class="ia-col-diasparado">Dias Parado</th>');
    htmlTable.push('<th class="ia-col-datadistribuicao">Data de Distribuicao</th>');
    htmlTable.push('</tr></thead><tbody>');

    for (const row of rows) {
      htmlTable.push('<tr>');
      htmlTable.push(this.renderTableCell('numeroordem', row.numero, row.dataDistribuicao));
      htmlTable.push(this.renderTableCell('status', row.status));
      htmlTable.push(`<td class="ia-col-origem">${this.escapeHtml(row.origem)}</td>`);
      htmlTable.push(`<td class="ia-col-diasparado">${this.escapeHtml(row.diasParado)}</td>`);
      htmlTable.push(this.renderTableCell('datadistribuicao', row.dataDistribuicao));
      htmlTable.push('</tr>');
    }

    htmlTable.push('</tbody></table></div>');
    return { html: htmlTable.join(''), nextIndex: i };
  }

  private parseLooseListingRow(line: string): { numero: string; status: string; origem: string; diasParado: string; dataDistribuicao: string } | null {
    const compact = line
      .replace(/[|]+/g, ' ')
      .replace(/[;,]+$/g, '')
      .replace(/\s+/g, ' ')
      .trim();

    const numeroMatch = compact.match(/^(\d{3,8})\b/);
    const dateMatch = compact.match(/(\d{2}\/\d{2}\/\d{4})\b/);
    if (!numeroMatch || !dateMatch || dateMatch.index === undefined) {
      return null;
    }

    const numero = numeroMatch[1].trim();
    const dataDistribuicao = dateMatch[1].trim();

    const middle = compact
      .slice(numeroMatch[0].length, dateMatch.index)
      .trim();

    if (!middle) {
      return null;
    }

    const parsedMiddle = this.parseListingMiddle(middle);
    if (!parsedMiddle) {
      return null;
    }

    return {
      numero,
      status: parsedMiddle.status,
      origem: parsedMiddle.origem,
      diasParado: parsedMiddle.diasParado,
      dataDistribuicao,
    };
  }

  private parseListingMiddle(value: string): { status: string; origem: string; diasParado: string } | null {
    if (!value) return null;

    const normalized = value.trim();
    const match = normalized.match(/^(.*?)\s*(\d{1,4})$/);
    if (!match) {
      return null;
    }

    const diasParado = (match[2] || '').trim();
    const left = (match[1] || '').trim();
    if (!left || !diasParado) {
      return null;
    }

    const leftTokens = left.split(/\s+/).filter(Boolean);
    if (leftTokens.length === 0) {
      return null;
    }

    let status = leftTokens[0];
    let origem = leftTokens.slice(1).join(' ');

    if (!origem) {
      const knownStatusOriginMatch = status.match(/^(Pendente|Concluido|Concluida|Cancelado|Cancelada|Negado|Negada)(Recadastro|Cadastro|Vistoria|Inspecao)$/i);
      if (knownStatusOriginMatch) {
        status = knownStatusOriginMatch[1];
        origem = knownStatusOriginMatch[2];
      }
    }

    if (!origem) {
      const genericSplit = status.match(/^([A-Za-zÀ-ÿ]+?)(Recadastro|Cadastro|Vistoria|Inspecao)$/i);
      if (genericSplit) {
        status = genericSplit[1];
        origem = genericSplit[2];
      }
    }

    if (!origem) {
      origem = '-';
    }

    return {
      status,
      origem,
      diasParado,
    };
  }

  private parseHeadingLine(line: string): { level: number; text: string } | null {
    const headingMatch = line.match(/^((?:#\s*){1,6})(.+)$/);
    if (!headingMatch) {
      return null;
    }

    const level = Math.min((headingMatch[1].match(/#/g) || []).length, 6);
    const text = (headingMatch[2] || '').trim();
    if (!text) {
      return null;
    }

    return { level, text };
  }

  private parseOperationalScenarioCard(lines: string[], startIndex: number): { html: string; nextIndex: number } {
    if (startIndex >= lines.length) {
      return { html: '', nextIndex: startIndex };
    }

    const headerLine = lines[startIndex].trim();
    const normalizedHeader = headerLine
      .replace(/^#{1,6}\s+/, '')
      .replace(/[*_`]/g, '')
      .replace(/:\s*$/, '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();

    if (!normalizedHeader.includes('leitura operacional do cenario')) {
      return { html: '', nextIndex: startIndex };
    }

    const paragraphs: string[] = [];
    let i = startIndex + 1;

    while (i < lines.length) {
      const line = lines[i].trim();

      if (!line) {
        if (paragraphs.length > 0) {
          break;
        }
        i++;
        continue;
      }

      if (/^--+$/.test(line) || /^\d+\.\s+/.test(line) || /^#{1,6}\s+/.test(line)) {
        break;
      }

      paragraphs.push(line);
      i++;
    }

    if (paragraphs.length === 0) {
      return { html: '', nextIndex: startIndex };
    }

    const htmlCard: string[] = [
      '<section class="ia-insight-card">',
      '<header class="ia-insight-header">',
      '<span class="ia-insight-icon"><i class="fas fa-lightbulb"></i></span>',
      '<h4 class="ia-insight-title">Leitura Operacional do Cenario</h4>',
      '</header>',
      '<div class="ia-insight-body">',
    ];

    for (const paragraph of paragraphs) {
      const escaped = this.escapeHtml(paragraph);
      htmlCard.push(`<p class="ia-insight-text">${this.formatInline(escaped)}</p>`);
    }

    htmlCard.push('</div></section>');
    return { html: htmlCard.join(''), nextIndex: i };
  }

  private parseKeyValueRow(line: string): Record<string, string> | null {
    // Remove lista numerada ou bullet no inicio da linha.
    const withoutBullet = line.replace(/^\s*(?:\d+\.\s+|[*-]\s+)/, '').trim();
    if (!withoutBullet.includes(':')) {
      return null;
    }

    // Remove markdown para facilitar a deteccao dos blocos de chave/valor.
    const cleaned = withoutBullet.replace(/\*\*/g, '').trim();

    // Captura posicoes das chaves conhecidas para suportar texto com e sem virgula.
    const keyFinder = /(numero\s+da\s+ordem|ordem\s+de\s+servi[cç]o|status|data\s+de\s+distribui[cç][aã]o|nome\s+do\s+vistoriador|nome\s+do\s+t[eé]cnico|nome\s+t[eé]cnico)\s*:/gi;
    const matches = Array.from(cleaned.matchAll(keyFinder));

    if (matches.length < 2) {
      return null;
    }

    const parsed: Record<string, string> = {};

    for (let i = 0; i < matches.length; i++) {
      const match = matches[i];
      const nextMatch = matches[i + 1];
      const rawKey = (match[1] || '').trim();
      const valueStart = (match.index ?? 0) + match[0].length;
      const valueEnd = nextMatch?.index ?? cleaned.length;
      const rawValue = cleaned
        .slice(valueStart, valueEnd)
        .trim()
        .replace(/^[,;\-\s]+/, '')
        .replace(/[;,\-\s]+$/, '')
        .trim();

      if (!rawKey || !rawValue) continue;

      const key = this.normalizeColumnKey(rawKey);
      if (!key) continue;
      parsed[key] = rawValue;
    }

    return Object.keys(parsed).length >= 2 ? parsed : null;
  }

  private normalizeColumnKey(key: string): string {
    const normalized = key
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove diacríticos
      .replace(/[^a-z0-9]/g, '') // Remove caracteres não alfanuméricos
      .replace(/^\d+/, ''); // Remove números no início

    // Mapeamento de chaves comuns
    if (normalized.includes('ordemdeservico') || normalized.includes('numeroordem') || normalized === 'numerodaordem') {
      return 'numeroordem';
    }

    if (normalized.includes('vistoriador')) {
      return 'nomevistoriador';
    }

    if (normalized.includes('tecnico') || normalized.includes('tecnica')) {
      return 'nometecnico';
    }

    if (normalized.includes('datadistribuicao') || normalized === 'datadedistribuicao') {
      return 'datadistribuicao';
    }

    if (normalized === 'status') {
      return 'status';
    }

    return normalized;
  }

  private getColumnLabelFromKey(key: string): string {
    const labels: Record<string, string> = {
      numeroordem: 'Numero da Ordem',
      status: 'Status',
      datadistribuicao: 'Data de Distribuicao',
      nomevistoriador: 'Nome do Vistoriador',
      nometecnico: 'Nome do Tecnico',
    };

    if (labels[key]) {
      return labels[key];
    }

    return key
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  private renderTableCell(columnKey: string, value: string, date?: string): string {
    const escapedValue = this.escapeHtml(value);

    if (columnKey === 'status') {
      const statusClass = this.getStatusClassName(value);
      return `<td class="ia-col-${columnKey}"><span class="ia-status-pill ${statusClass}">${escapedValue}</span></td>`;
    }

    if (columnKey === 'numeroordem' && value) {
      const dataAttr = date ? ` data-vistoria-data="${this.escapeHtml(date)}"` : '';
      return `<td class="ia-col-${columnKey}">${escapedValue}<button class="ia-btn-ver" data-vistoria-numero="${escapedValue}"${dataAttr} title="Ver ordem de serviço">Ver</button></td>`;
    }

    return `<td class="ia-col-${columnKey}">${escapedValue}</td>`;
  }

  private getStatusClassName(value: string): string {
    const normalized = value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();

    if (normalized.includes('conclu')) {
      return 'ia-status-success';
    }

    if (normalized.includes('pendente')) {
      return 'ia-status-warning';
    }

    if (normalized.includes('cancel')) {
      return 'ia-status-danger';
    }

    return 'ia-status-neutral';
  }

  private formatInline(text: string): string {
    return text
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/`([^`]+)`/g, '<code>$1</code>')
      .replace(/\b(relatorio_geral|ordens_servico|ranking_vistoriadores)\b/gi, (_match, token: string) => {
        return `<span class="ia-tag">${this.formatReportTypeLabel(token)}</span>`;
      })
      .replace(/\b(tipo de relatorio|tipo de relatório|periodo|período|data de inicio|data de início|data de fim)\b/gi, '<span class="ia-required">$1</span>')
      .replace(/(\d{2,})/g, '<span class="ia-number">$1</span>');
  }

  private formatReportTypeLabel(token: string): string {
    const normalized = token.toLowerCase();

    if (normalized === 'relatorio_geral') {
      return 'Relatorio Geral';
    }

    if (normalized === 'ordens_servico') {
      return 'Ordens de Servico';
    }

    if (normalized === 'ranking_vistoriadores') {
      return 'Ranking de Vistoriadores';
    }

    return token;
  }

  private buildReportOptionsListHtml(line: string): string | undefined {
    if (!this.isRequestGuidanceLine(line)) {
      return undefined;
    }

    const options: string[] = [];

    if (/(relatorio_geral|relat[oó]rio\s+geral)/i.test(line)) {
      options.push('Relatorio Geral');
    }

    if (/(ordens_servico|ordens\s+de\s+servi[cç]o)/i.test(line)) {
      options.push('Ordens de Servico');
    }

    if (/(ranking_vistoriadores|ranking\s+de\s+vistoriadores)/i.test(line)) {
      options.push('Ranking de Vistoriadores');
    }

    if (options.length < 2) {
      return undefined;
    }

    const uniqueOptions = Array.from(new Set(options));
    const listItems = uniqueOptions.map((item) => `<li>${item}</li>`).join('');
    return `<ul class="ia-options-list">${listItems}</ul>`;
  }

  private isHighlightLine(line: string): boolean {
    const normalized = line.toLowerCase();

    if (/\*\*[^*]+:\*\*\s*\d+/i.test(line)) {
      return true;
    }

    return /(total|resumo|pendentes|finalizados|invalidos|inv[aá]lidos|negados|top\s*\d+)/i.test(normalized);
  }

  private isRequestGuidanceLine(line: string): boolean {
    const normalized = line.toLowerCase();
    return /(preciso|informa[cç][aã]o|gerar|relat[oó]rio|tipo de relat[oó]rio|per[ií]odo|data de in[ií]cio|data de fim)/i.test(normalized);
  }

  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /** Detects vistoria list item patterns and renders a compact card */
  private tryRenderVistoriaCard(text: string, origemContexto: string = ''): string | null {
    let numero: string, origem: string, status: string, data: string, dias: string;

    // Strip markdown bold markers so patterns match regardless of **bold** formatting
    const clean = text.replace(/\*\*/g, '');

    // Pattern 1: "Vistoria número 03376 (Origem: Recadastro, Status: Pendente), distribuída em 11/11/2025 e parada há 149 dias."
    const m1 = clean.match(
      /Vistoria\s+n[uú]mero\s+(\d+)\s*\(Origem:\s*([^,]+),\s*Status:\s*([^)]+)\),?\s*distribu[íi]da\s+em\s+(\d{2}\/\d{2}\/\d{4})\s+e\s+parada\s+h[áa]\s+(\d+)\s*dias/i
    );
    if (m1) {
      [, numero, origem, status, data, dias] = m1;
      return this.buildVistoriaCardHtml(numero, origem, status, data, dias);
    }

    // Pattern 2: "NumeroOrdem: 03376, Origem: Recadastro, Status: Pendente, DataDistribuicao: 11/11/2025, DiasParado: 149"
    const m2 = clean.match(
      /NumeroOrdem:\s*(\d+).*?Origem:\s*([^,]+).*?Status:\s*([^,]+).*?DataDistribui[cç][aã]o:\s*(\d{2}\/\d{2}\/\d{4}).*?DiasParado:\s*(\d+)/i
    );
    if (m2) {
      [, numero, origem, status, data, dias] = m2;
      return this.buildVistoriaCardHtml(numero, origem, status, data, dias);
    }

    // Pattern 3: "Número da Vistoria: 03376, Distribuída em: 11/11/2025, Parada há: 149 dias, Status: Pendente"
    const m3 = clean.match(
      /N[uú]mero\s+da\s+Vistoria:\s*(\d+).*?Distribu[ií]da\s+em:\s*(\d{2}\/\d{2}\/\d{4}).*?Parada\s+h[áa]:\s*(\d+)\s*dias.*?Status:\s*([A-Za-zÀ-ú]+)/i
    );
    if (m3) {
      [, numero, data, dias, status] = m3;
      return this.buildVistoriaCardHtml(numero, origemContexto, status, data, dias);
    }

    return null;
  }

  private buildVistoriaCardHtml(numero: string, origem: string, status: string, data: string, dias: string): string {
    const diasNum = parseInt(dias, 10);
    const urgencyClass = diasNum >= 120 ? 'ia-vc-urgency-high' : diasNum >= 60 ? 'ia-vc-urgency-med' : 'ia-vc-urgency-low';
    const origemHtml = origem && origem.trim() ? `<span class="ia-vc-origem">${this.escapeHtml(origem.trim())}</span>` : '';

    return `<div class="ia-vistoria-card ${urgencyClass}">
  <div class="ia-vc-number">#${this.escapeHtml(numero)}</div>
  <div class="ia-vc-meta">
    ${origemHtml}
    <span class="ia-vc-status">${this.escapeHtml(status.trim())}</span>
  </div>
  <div class="ia-vc-footer">
    <span class="ia-vc-data"><i class="fas fa-calendar-alt"></i> ${this.escapeHtml(data)}</span>
    <span class="ia-vc-dias">${this.escapeHtml(dias)}d parada</span>
  </div>
</div>`;
  }

  /**
   * When 2+ consecutive numbered structured-data rows exist, renders them as a
   * compact table instead of individual cards. Falls back (returns '') for 0–1 rows
   * so the single-card path still handles lone records.
   */
  private parseVistoriadorRankingTable(lines: string[], startIndex: number): { html: string; nextIndex: number } {
    const rows: Array<{ rank: string; vistoriador: string; total: string; totalValue: number }> = [];
    let i = startIndex;

    while (i < lines.length) {
      const line = lines[i].trim();
      if (!line) {
        break;
      }

      const orderedMatch = line.match(/^(\d+)\.\s+(.*)$/);
      if (!orderedMatch) {
        break;
      }

      const rankingMatch = this.parseRankingItem(orderedMatch[2]);

      if (!rankingMatch) {
        break;
      }

      rows.push({
        rank: orderedMatch[1],
        vistoriador: rankingMatch.label.trim(),
        total: rankingMatch.total.trim(),
        totalValue: rankingMatch.totalValue,
      });
      i++;
    }

    if (rows.length < 2) {
      return { html: '', nextIndex: startIndex };
    }

    const pdfPrompt = `Por favor, gere um PDF com o ranking de ${rows.length} itens exibido na última resposta.`;
    const maxTotal = Math.max(...rows.map((row) => row.totalValue));
    const totalSum = rows.reduce((sum, row) => sum + row.totalValue, 0);
    const topThree = rows.slice(0, 3);
    const titleLabel = this.detectRankingLabel(lines, startIndex) ?? 'Ranking';

    const medalClass = (rank: number): string => {
      if (rank === 1) return 'ia-ranking-medal-gold';
      if (rank === 2) return 'ia-ranking-medal-silver';
      if (rank === 3) return 'ia-ranking-medal-bronze';
      return 'ia-ranking-medal-neutral';
    };

    const medalLabel = (rank: number): string => {
      if (rank === 1) return '1º lugar';
      if (rank === 2) return '2º lugar';
      if (rank === 3) return '3º lugar';
      return `${rank}º lugar`;
    };

    const renderPodiumCard = (row: { rank: string; vistoriador: string; total: string; totalValue: number }): string => {
      const rankNumber = parseInt(row.rank, 10) || 0;
      const width = maxTotal > 0 ? Math.max(12, Math.round((row.totalValue / maxTotal) * 100)) : 12;
      const medal = medalClass(rankNumber);
      const label = medalLabel(rankNumber);

      return `
        <article class="ia-ranking-podium-card ${rankNumber === 1 ? 'ia-ranking-podium-card-champion' : ''} ${medal}">
          <div class="ia-ranking-podium-top">
            <span class="ia-ranking-podium-rank">${this.escapeHtml(label)}</span>
            <span class="ia-ranking-podium-badge"><i class="fas fa-trophy"></i></span>
          </div>
          <div class="ia-ranking-podium-name">${this.escapeHtml(row.vistoriador)}</div>
          <div class="ia-ranking-podium-total">${this.escapeHtml(this.formatRankingNumber(row.totalValue))} vistorias</div>
          <div class="ia-ranking-podium-track">
            <span class="ia-ranking-podium-fill" style="width:${width}%"></span>
          </div>
        </article>`;
    };

    const renderListRow = (row: { rank: string; vistoriador: string; total: string; totalValue: number }): string => {
      const rankNumber = parseInt(row.rank, 10) || 0;
      const width = maxTotal > 0 ? Math.max(8, Math.round((row.totalValue / maxTotal) * 100)) : 8;
      return `
        <div class="ia-ranking-row">
          <div class="ia-ranking-row-rank">${this.escapeHtml(row.rank)}</div>
          <div class="ia-ranking-row-body">
            <div class="ia-ranking-row-head">
              <span class="ia-ranking-row-name">${this.escapeHtml(row.vistoriador)}</span>
              <span class="ia-ranking-row-total">${this.escapeHtml(this.formatRankingNumber(row.totalValue))}</span>
            </div>
            <div class="ia-ranking-row-track"><span class="ia-ranking-row-fill ${rankNumber <= 3 ? 'ia-ranking-row-fill-elite' : ''}" style="width:${width}%"></span></div>
          </div>
        </div>`;
    };

    const podiumOrder = [rows[1], rows[0], rows[2]].filter((row): row is (typeof rows)[number] => !!row);

    let html = '<section class="ia-ranking-showcase">';
    html += `<div class="ia-ranking-hero">`;
    html += `<div class="ia-ranking-hero-badge"><i class="fas fa-ranking-star"></i> ${this.escapeHtml(titleLabel)} em destaque</div>`;
    html += `<h3 class="ia-ranking-hero-title">Desempenho em destaque</h3>`;
    html += `<p class="ia-ranking-hero-subtitle">${rows.length} itens avaliados, ${this.escapeHtml(this.formatRankingNumber(totalSum))} vistorias acumuladas</p>`;
    html += `<div class="ia-ranking-hero-metrics">`;
    html += `<div class="ia-ranking-metric"><span class="ia-ranking-metric-label">Líder</span><strong>${this.escapeHtml(rows[0].vistoriador)}</strong></div>`;
    html += `<div class="ia-ranking-metric"><span class="ia-ranking-metric-label">Maior total</span><strong>${this.escapeHtml(this.formatRankingNumber(rows[0].totalValue))}</strong></div>`;
    html += `<div class="ia-ranking-metric"><span class="ia-ranking-metric-label">Top 3 somado</span><strong>${this.escapeHtml(this.formatRankingNumber(rows.slice(0, 3).reduce((sum, row) => sum + row.totalValue, 0)))}</strong></div>`;
    html += `</div>`;
    html += `<div class="ia-table-toolbar ia-ranking-toolbar"><span class="ia-table-toolbar-title"><i class="fas fa-chart-line"></i> ${rows.length} vistoriadores encontrados</span><button class="ia-btn-pdf" data-pdf-prompt="${pdfPrompt.replace(/"/g, '&quot;')}"><i class="fas fa-file-pdf"></i> Gerar PDF</button></div>`;
    html += `</div>`;

    html += `<div class="ia-ranking-podium">`;
    for (const row of podiumOrder) {
      html += renderPodiumCard(row);
    }
    html += `</div>`;

    html += `<div class="ia-ranking-list">`;
    html += `<div class="ia-ranking-list-title">Ranking completo</div>`;
    for (const row of rows) {
      html += renderListRow(row);
    }
    html += `</div>`;
    html += `</section>`;

    return { html, nextIndex: i };
  }

  private parseRankingItem(text: string): { label: string; total: string; totalValue: number } | null {
    const cleaned = text.replace(/\*\*/g, '').trim();

    // Pattern: "vistoriador: Eliene Reis, vistorias_validadas: 1544" or "Vistoriador: Eliene, TotalVistoriasValidadas: 1544"
    const kvMatch = cleaned.match(
      /^(?:Cidade|Vistoriador|Vistoriadora|Colaborador|Usu[aá]rio)\s*:\s*([^,]+),\s*(?:TotalVistoriasConcluidas|TotalVistoriasValidadas|Total\s+de\s+Vistorias(?:\s+(?:Conclu[ií]das|Validadas))?|TotalVistorias|Total|vistorias[_\s]?validadas|vistorias[_\s]?conclu[ií]das|quantidade|qtd)\s*:\s*([\d.]+)\s*$/i
    );
    if (kvMatch) {
      return {
        label: kvMatch[1].trim(),
        total: kvMatch[2].trim(),
        totalValue: parseInt(kvMatch[2].replace(/[^\d]/g, ''), 10) || 0,
      };
    }

    const narrativeMatch = cleaned.match(/^([^:]+):\s*(?:Com\s+)?([\d.]+)\s+vistorias?\s+conclu[ií]das?(?:,.*)?$/i);
    if (narrativeMatch) {
      return {
        label: narrativeMatch[1].trim(),
        total: narrativeMatch[2].trim(),
        totalValue: parseInt(narrativeMatch[2].replace(/[^\d]/g, ''), 10) || 0,
      };
    }

    const looseMatch = cleaned.match(/^([^:]{2,120}):\s*(?:.*?)([\d][\d.]*)\b(?:.*)?$/i);
    if (looseMatch) {
      return {
        label: looseMatch[1].trim(),
        total: looseMatch[2].trim(),
        totalValue: parseInt(looseMatch[2].replace(/[^\d]/g, ''), 10) || 0,
      };
    }

    return null;
  }

  private detectRankingLabel(lines: string[], startIndex: number): string | null {
    for (let i = Math.max(0, startIndex - 3); i < Math.min(lines.length, startIndex + 2); i++) {
      const normalized = lines[i]
        .replace(/\*\*/g, '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim();

      if (normalized.includes('cidade')) return 'Ranking de cidades';
      if (normalized.includes('vistoriador')) return 'Ranking de vistoriadores';
      if (normalized.includes('ranking')) return 'Ranking';
    }

    return null;
  }

  private formatRankingNumber(value: number): string {
    if (!Number.isFinite(value)) {
      return '0';
    }

    return new Intl.NumberFormat('pt-BR').format(value);
  }

  /**
   * When 2+ consecutive numbered structured-data rows exist, renders them as a
   * compact table instead of individual cards. Falls back (returns '') for 0–1 rows
   * so the single-card path still handles lone records.
   */
  private parseMultiStructuredDataTable(lines: string[], startIndex: number): { html: string; nextIndex: number } {
    const rows: { key: string; value: string }[][] = [];
    let i = startIndex;

    while (i < lines.length) {
      const line = lines[i].trim();
      if (!line) break;
      const orderedMatch = line.match(/^\d+\.\s+(.*)$/);
      if (!orderedMatch) break;
      if (!this.isStructuredDataRow(orderedMatch[1])) break;
      rows.push(this.parseKvPairs(orderedMatch[1]));
      i++;
    }

    if (rows.length < 2) return { html: '', nextIndex: startIndex };

    // Discover all column keys in order of first appearance
    const keys: string[] = [];
    for (const row of rows) {
      for (const { key } of row) {
        if (!keys.includes(key)) keys.push(key);
      }
    }

    const humanize = (k: string): string => {
      const map: Record<string, string> = {
        NomeConsumidor: 'Nome', Matricula: 'Matrícula', NumeroOrdem: 'Nº Ordem',
        NumeroImovel: 'Nº Imóvel', Status: 'Status', Origem: 'Origem',
        DataDistribuicao: 'Distribuída em', Vistoriador: 'Vistoriador',
        DataHoraRecadastramento: 'Recadastrado em', AnalisadoPor: 'Analisado por',
        CPF: 'CPF', Telefone: 'Telefone', Email: 'E-mail',
      };
      return map[k] ?? k.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim();
    };

    const statusBadge = (val: string): string => {
      const s = val.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      const cls = s.includes('pendente') ? 'ia-badge-pending'
        : s.includes('conclu') ? 'ia-badge-done'
        : s.includes('neg') ? 'ia-badge-negado'
        : s.includes('retornar') || s.includes('retorno') ? 'ia-badge-retornar'
        : s.includes('an') ? 'ia-badge-analysis'
        : 'ia-badge-default';
      return `<span class="ia-badge ${cls}">${this.escapeHtml(val)}</span>`;
    };

    const pdfPrompt = `Por favor, gere um PDF com os ${rows.length} registros da tabela que foi exibida na última resposta.`;
    let html = '<div class="ia-vistoria-table-wrap">';
    html += `<div class="ia-table-toolbar"><span class="ia-table-toolbar-title"><i class="fas fa-table"></i> ${rows.length} registros encontrados</span><button class="ia-btn-pdf" data-pdf-prompt="${pdfPrompt.replace(/"/g, '&quot;')}"><i class="fas fa-file-pdf"></i> Gerar PDF</button></div>`;
    const hasNumeroOrdem = keys.includes('NumeroOrdem');

    html += '<div class="ia-vistoria-table-body">';
    html += '<table class="ia-vistoria-table ia-vistoria-table-auto"><thead><tr class="ia-vt-col-row">';
    for (const k of keys) html += `<th>${humanize(k)}</th>`;
    if (hasNumeroOrdem) html += '<th class="ia-vt-col-ver"></th>';
    html += '</tr></thead><tbody>';

    for (const row of rows) {
      const rowMap: Record<string, string> = {};
      for (const { key, value } of row) rowMap[key] = value;
      const dataDistRow = rowMap['DataDistribuicao'] ?? '';
      html += '<tr>';
      for (const k of keys) {
        const val = rowMap[k] ?? '—';
        const isStatus = k.toLowerCase() === 'status';
        const isOrigem = k.toLowerCase() === 'origem';
        let cellContent: string;
        if (isStatus) {
          cellContent = statusBadge(val);
        } else if (isOrigem) {
          const o = val.toLowerCase();
          const cls = o.includes('recadastro') ? 'ia-badge-recadastro' : o.includes('cadastro') ? 'ia-badge-cadastro' : 'ia-badge-default';
          cellContent = `<span class="ia-badge ${cls}">${this.escapeHtml(val)}</span>`;
        } else {
          cellContent = this.escapeHtml(val);
        }
        html += `<td${k === 'NumeroOrdem' || k === 'Matricula' ? ' class="ia-vt-num"' : ''}>${cellContent}</td>`;
      }
      if (hasNumeroOrdem) {
        const numVal = rowMap['NumeroOrdem'] ?? '';
        const dataAttr = dataDistRow ? ` data-vistoria-data="${this.escapeHtml(dataDistRow)}"` : '';
        html += numVal && numVal !== '—'
          ? `<td class="ia-vt-col-ver"><button class="ia-btn-ver" data-vistoria-numero="${this.escapeHtml(numVal)}"${dataAttr}>Ver</button></td>`
          : '<td class="ia-vt-col-ver"></td>';
      }
      html += '</tr>';
    }

    html += '</tbody></table></div></div>';
    return { html, nextIndex: i };
  }

  /** Detects "Key: Value, Key: Value, ..." structured SQL result rows */
  private isStructuredDataRow(text: string): boolean {
    const kvPattern = /^[A-Za-z][A-Za-z0-9_]+:\s/;
    const segments = text.split(/,\s*(?=[A-Za-z][A-Za-z0-9_]+:\s)/);
    return segments.length >= 3 && kvPattern.test(text);
  }

  /** Parses a structured key:value row into a styled HTML card */
  private renderStructuredDataCard(text: string): string {
    const pairs = this.parseKvPairs(text);
    if (!pairs.length) return `<p class="ia-md-paragraph">${this.formatInline(this.escapeHtml(text))}</p>`;

    // Extrair data de distribuição do card para usar no botão Ver
    const dataDistribuicaoPair = pairs.find(p =>
      /distribui/i.test(p.key.normalize('NFD').replace(/[\u0300-\u036f]/g, ''))
    );
    const dataDistribuicao = dataDistribuicaoPair?.value ?? '';

    const groups = this.groupKvFields(pairs);
    let cardHtml = '<div class="ia-data-card">';

    for (const group of groups) {
      if (!group.fields.length) continue;
      cardHtml += `<div class="ia-data-group">`;
      if (group.label) {
        cardHtml += `<div class="ia-data-group-label"><i class="${group.icon}"></i> ${group.label}</div>`;
      }
      cardHtml += '<div class="ia-data-fields">';
      for (const { key, value } of group.fields) {
        const safeVal = this.escapeHtml(value);
        const keyNorm = key.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
        const isNumeroOrdem = /ordem|vistoria/i.test(keyNorm) && !/nome|vistoriador|tecnico|analisado/i.test(keyNorm);
        let displayValue: string;
        if (value === 'null' || value === '') {
          displayValue = '<span class="ia-val-null">—</span>';
        } else if (isNumeroOrdem) {
          const dataAttr = dataDistribuicao ? ` data-vistoria-data="${this.escapeHtml(dataDistribuicao)}"` : '';
          displayValue = `<span class="ia-val">${safeVal}<button class="ia-btn-ver" data-vistoria-numero="${safeVal}"${dataAttr} title="Ver ordem de serviço">Ver</button></span>`;
        } else {
          displayValue = `<span class="ia-val">${safeVal}</span>`;
        }
        cardHtml += `<div class="ia-data-field"><span class="ia-field-key">${this.humanizeFieldKey(key)}</span>${displayValue}</div>`;
      }
      cardHtml += '</div></div>';
    }

    cardHtml += '</div>';
    return cardHtml;
  }

  private parseKvPairs(text: string): { key: string; value: string }[] {
    const result: { key: string; value: string }[] = [];
    const segments = text.split(/,\s*(?=[A-Za-z][A-Za-z0-9_]+:\s)/);
    for (const seg of segments) {
      const colonIdx = seg.indexOf(':');
      if (colonIdx === -1) continue;
      const key = seg.slice(0, colonIdx).trim();
      const value = seg.slice(colonIdx + 1).trim();
      if (key) result.push({ key, value });
    }
    return result;
  }

  private groupKvFields(pairs: { key: string; value: string }[]): { label: string; icon: string; fields: { key: string; value: string }[] }[] {
    const defs: { label: string; icon: string; keys: string[] }[] = [
      { label: 'Identificação',  icon: 'fas fa-id-card',        keys: ['NomeConsumidor','CPF','Matricula','CicloDeLeitura','NumeroImovel','LojLoc'] },
      { label: 'Contato',        icon: 'fas fa-phone',           keys: ['Email','Telefone'] },
      { label: 'Endereço',       icon: 'fas fa-map-marker-alt',  keys: ['Endereco_Logradouro','Endereco_Numero','Endereco_Complemento','Endereco_Bairro','Endereco_Cidade','Endereco_Estado','Endereco_Cep'] },
      { label: 'Serviço',        icon: 'fas fa-concierge-bell',  keys: ['TipoUtilizacao','ConcessaoExpediente','SistemaAbastecimento','TipoAbastecimento','NumeroDeEconomias','TipoEconomia','PossuiDebito','ValorDebito','DataInicioDebito','Ramal','Direto','Filtro'] },
      { label: 'Infraestrutura', icon: 'fas fa-water',           keys: ['HaCisterna','CapacidadeCisternaInformada','HaReservatorioElevado','CapacidadeReservatorioElevadoInformada'] },
      { label: 'Hidrômetro',     icon: 'fas fa-gauge-high',      keys: ['NumeroHidrometro','Marcacao','SeloReloj','SeloVirola'] },
      { label: 'Vistoria',       icon: 'fas fa-clipboard-check', keys: ['DataHoraRecadastramento','Vistoriador','AnalisadoPor'] },
    ];

    const groups = defs.map(d => ({ ...d, fields: [] as { key: string; value: string }[] }));
    const catchAll: { label: string; icon: string; fields: { key: string; value: string }[]; keys: string[] } = { label: '', icon: '', keys: [], fields: [] };
    const assigned = new Set<string>();

    for (const group of groups) {
      for (const key of group.keys) {
        const pair = pairs.find(p => p.key === key);
        if (pair) { group.fields.push(pair); assigned.add(key); }
      }
    }
    for (const pair of pairs) {
      if (!assigned.has(pair.key)) catchAll.fields.push(pair);
    }

    return [...groups, catchAll];
  }

  private humanizeFieldKey(key: string): string {
    const map: Record<string, string> = {
      NomeConsumidor: 'Nome', CPF: 'CPF', Matricula: 'Matrícula', CicloDeLeitura: 'Ciclo Leitura',
      NumeroImovel: 'Nº Imóvel', LojLoc: 'Loja', Email: 'E-mail', Telefone: 'Telefone',
      Endereco_Logradouro: 'Logradouro', Endereco_Numero: 'Número', Endereco_Complemento: 'Complemento',
      Endereco_Bairro: 'Bairro', Endereco_Cidade: 'Cidade', Endereco_Estado: 'Estado', Endereco_Cep: 'CEP',
      TipoUtilizacao: 'Tipo Utilização', ConcessaoExpediente: 'Concessão', SistemaAbastecimento: 'Sistema Abast.',
      TipoAbastecimento: 'Tipo Abast.', NumeroDeEconomias: 'Economias', TipoEconomia: 'Tipo Economia',
      PossuiDebito: 'Possui Débito', ValorDebito: 'Valor Débito', DataInicioDebito: 'Início Débito',
      Ramal: 'Ramal', Direto: 'Direto', Filtro: 'Filtro',
      HaCisterna: 'Cisterna', CapacidadeCisternaInformada: 'Cap. Cisterna',
      HaReservatorioElevado: 'Reservatório', CapacidadeReservatorioElevadoInformada: 'Cap. Reservatório',
      NumeroHidrometro: 'Nº Hidrômetro', Marcacao: 'Marcação', SeloReloj: 'Selo Reloj', SeloVirola: 'Selo Virola',
      DataHoraRecadastramento: 'Recadastrado em', Vistoriador: 'Vistoriador', AnalisadoPor: 'Analisado por',
    };
    return map[key] ?? key.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim();
  }

  sendMessage(): void {
    const text = (this.chatForm.get('message')?.value || '').trim();
    if (!text || this.isTyping) return;

    const draftMessage = text;

    this.addMessage('user', text);
    this.chatForm.reset();
    this.resetInputHeight();
    this.isTyping = true;
    this.shouldScroll = true;

    this.iaService.enviarMensagem({ mensagem: text, sessionId: this.sessionId }).subscribe({
      next: (res) => {
        this.isTyping = false;
        if (res.sessionId) {
          this.sessionId = res.sessionId;
          this.persistChatState();
        }
        this.addMessage('assistant', res.resposta, this.toAttachment(res.arquivoGerado));
      },
      error: () => {
        this.isTyping = false;
        this.chatForm.patchValue({ message: draftMessage });
        if (this.messageInput) {
          const textarea = this.messageInput.nativeElement;
          this.resizeTextarea(textarea);
          textarea.focus();
          const len = textarea.value.length;
          textarea.setSelectionRange(len, len);
        }
        this.addMessage('assistant', 'Desculpe, não consegui processar sua solicitação no momento. Tente novamente em instantes.');
      },
    });
  }

  private toAttachment(file?: ChatGeneratedFile): ChatAttachment | undefined {
    if (!file) return undefined;

    return {
      nomeArquivo: file.nomeArquivo,
      mimeType: file.mimeType,
      conteudoBase64: file.conteudoBase64,
    };
  }

  sendQuickAction(prompt: string): void {
    if (this.isTyping) return;
    this.chatForm.patchValue({ message: prompt });
    this.sendMessage();
  }

  copyMessage(text: string): void {
    navigator.clipboard.writeText(text).catch(() => {});
  }

  onChatBodyClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    const pdfBtn = target.closest('[data-pdf-prompt]') as HTMLElement | null;
    if (pdfBtn) {
      const prompt = pdfBtn.getAttribute('data-pdf-prompt');
      if (prompt) this.sendQuickAction(prompt);
      return;
    }

    const verBtn = target.closest('[data-vistoria-numero]') as HTMLElement | null;
    if (verBtn) {
      const numero = verBtn.getAttribute('data-vistoria-numero');
      if (numero) {
        const dataRaw = verBtn.getAttribute('data-vistoria-data') ?? '';
        // Extrair apenas a parte da data (YYYY-MM-DD ou DD/MM/YYYY)
        let dataInicio = '2020-01-01T00:00:00';
        let dataFim = new Date().toISOString().split('T')[0] + 'T23:59:59';
        if (dataRaw) {
          // Suporta formatos: "2026-03-06 08:53:27", "2026-03-06T08:53:27", "06/03/2026"
          let dia: string | null = null;
          const isoMatch = dataRaw.match(/(\d{4}-\d{2}-\d{2})/);
          const brMatch = dataRaw.match(/(\d{2})\/(\d{2})\/(\d{4})/);
          if (isoMatch) {
            dia = isoMatch[1];
          } else if (brMatch) {
            dia = `${brMatch[3]}-${brMatch[2]}-${brMatch[1]}`;
          }
          if (dia) {
            dataInicio = `${dia}T00:00:00`;
            dataFim = `${dia}T23:59:59`;
          }
        }
        this.router.navigate(['/admin/ordem-servico'], {
          queryParams: {
            filtroNumeroOrdem: numero,
            filtroDataInicio: dataInicio,
            filtroDataFim: dataFim,
          }
        });
      }
    }
  }

  clearChat(): void {
    const currentSessionId = this.sessionId ?? this.getPersistedSessionId();

    this.messages = [];
    this.sessionId = null;
    this.messageIdCounter = 0;
    this.clearPersistedChatState();
    this.addMessage('assistant', 'Conversa reiniciada. Como posso te ajudar?');

    if (!currentSessionId) return;

    // Limpa no backend em segundo plano para nao bloquear a UI.
    this.iaService.limparSessao(currentSessionId).subscribe();
  }

  private getPersistedSessionId(): string | null {
    try {
      const raw = localStorage.getItem(this.getStorageKey());
      if (!raw) return null;

      const parsed = JSON.parse(raw) as PersistedChatState;
      if (!parsed || typeof parsed.sessionId !== 'string') {
        return null;
      }

      const normalized = parsed.sessionId.trim();
      return normalized || null;
    } catch {
      return null;
    }
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  onInput(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    this.resizeTextarea(textarea);
  }

  private resetInputHeight(): void {
    if (!this.messageInput) return;
    const textarea = this.messageInput.nativeElement;
    textarea.style.height = '44px';
    textarea.style.overflowY = 'hidden';
  }

  private resizeTextarea(textarea: HTMLTextAreaElement): void {
    const maxHeight = 140;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }

  private getStorageKey(): string {
    const usuario = this.authService.getUsuario();
    const usuarioId = (usuario?.usuarioId || localStorage.getItem('usuarioId') || 'anonimo') as string;
    return `${InteligenciaArtificialComponent.STORAGE_KEY_PREFIX}:${usuarioId}`;
  }

  private persistChatState(): void {
    try {
      const state: PersistedChatState = {
        sessionId: this.sessionId,
        messages: this.messages.map((m) => ({
          id: m.id,
          role: m.role,
          content: m.content,
          timestamp: m.timestamp.toISOString(),
        })),
      };
      localStorage.setItem(this.getStorageKey(), JSON.stringify(state));
    } catch {}
  }

  private restoreChatState(): boolean {
    try {
      const raw = localStorage.getItem(this.getStorageKey());
      if (!raw) return false;

      const parsed = JSON.parse(raw) as PersistedChatState;
      if (!parsed || !Array.isArray(parsed.messages) || parsed.messages.length === 0) {
        return false;
      }

      this.sessionId = typeof parsed.sessionId === 'string' ? parsed.sessionId : null;
      this.messages = parsed.messages.map((m) => {
        let safeContent: SafeHtml | undefined = undefined;
        
        if (m.role === 'assistant') {
          const formatted = this.formatMessage(m.content);
          safeContent = this.sanitizer.bypassSecurityTrustHtml(formatted);
        }
        
        return {
          id: m.id,
          role: m.role,
          content: m.content,
          safeContent,
          timestamp: new Date(m.timestamp),
        };
      });
      this.messageIdCounter = this.messages.reduce((max, m) => Math.max(max, m.id), 0);
      return true;
    } catch {
      return false;
    }
  }

  private clearPersistedChatState(): void {
    try {
      localStorage.removeItem(this.getStorageKey());
    } catch {}
  }
}
